// PersonCard.jsx
import React, { useState, useRef } from 'react';
import { EditedPhoto } from './EditedPhoto';
import './PersonCard.css';
import { FAMILY_COLORS } from '../utils/colorSystem';

const REL_OPTIONS = [
  { type: 'parent-child', direction: 'child',   label: 'Kind' },
  { type: 'parent-child', direction: 'parent',  label: 'Elternteil' },
  { type: 'sibling',      direction: 'sibling', label: 'Geschwister' },
  { type: 'partner',      direction: 'partner', label: 'Partner' },
  { type: 'ex-partner',   direction: 'ex',      label: 'Expartner' },
];

// SVG-Symbole
export function SymbolShape({ type, color = '#555', stroke, strokeWidth, size = 11 }) {
  const s = size;
  const sw = strokeWidth || 0;
  const props = stroke ? { fill: color, stroke, strokeWidth: sw } : { fill: color };
  switch (type) {
    case 'square':
      return <rect x="1" y="1" width={s-2} height={s-2} rx="1" {...props}/>;
    case 'circle':
      return <circle cx={s/2} cy={s/2} r={s/2-1} {...props}/>;
    case 'triangle':
      return <polygon points={`${s/2},1 ${s-1},${s-1} 1,${s-1}`} {...props}/>;
    case 'rectangle':
      return <rect x="1" y={s*0.25} width={s-2} height={s*0.5} rx="1" {...props}/>;
    case 'diamond':
      return <polygon points={`${s/2},1 ${s-1},${s/2} ${s/2},${s-1} 1,${s/2}`} {...props}/>;
    case 'pentagon': {
      const cx=s/2, cy=s/2, r=s/2-1;
      const pts=[0,1,2,3,4].map(i=>{const a=(i*72-90)*Math.PI/180;return `${(cx+r*Math.cos(a)).toFixed(1)},${(cy+r*Math.sin(a)).toFixed(1)}`;}).join(' ');
      return <polygon points={pts} {...props}/>;
    }
    case 'trapezoid':
      return <polygon points={`${s*0.2},${s-1} ${s*0.8},${s-1} ${s*0.65},1 ${s*0.35},1`} {...props}/>;
    case 'parallelogram':
      return <polygon points={`${s*0.25},${s-1} ${s-1},${s-1} ${s*0.75},1 1,1`} {...props}/>;
    default: return null;
  }
}

function SymbolBadge({ type, color, size = 11, outline = false }) {
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block',flexShrink:0}}>
      {outline
        ? <SymbolShape type={type} color="none" stroke={color} strokeWidth={1.5} size={size}/>
        : <SymbolShape type={type} color={color} size={size}/>
      }
    </svg>
  );
}

// Mehrfarbiger Rahmen: ersetzt den CSS-Rahmen vollständig
// Zeichnet einen gerundeten Rechteck-Rahmen aufgeteilt in N Segmente
function MultiColorBorder({ colors }) {
  if (!colors?.length) return null;
  const n = colors.length, W = 140, H = 160, bw = 9, r = 14;
  const inset = bw / 2; // Pfad nach innen verschieben damit Strich auf dem Rand liegt
  const iW = W - 2*inset, iH = H - 2*inset, iR = Math.max(1, r - inset);
  // Umfang des gerundeten Rechtecks (Näherung)
  const straight = 2 * (iW - 2*iR) + 2 * (iH - 2*iR);
  const corners  = 2 * Math.PI * iR;
  const perimeter = straight + corners;
  const segLen = perimeter / n;

  const ptOnRounded = (t) => {
    t = ((t % perimeter) + perimeter) % perimeter;
    const topStraight   = iW - 2*iR;
    const cornerArc     = (Math.PI / 2) * iR;
    const rightStraight = iH - 2*iR;
    const bottomStr     = iW - 2*iR;
    const leftStraight  = iH - 2*iR;
    let acc = 0;
    if (t < acc + topStraight) return [inset + iR + (t-acc), inset];
    acc += topStraight;
    if (t < acc + cornerArc) { const a=(t-acc)/iR-Math.PI/2; return [inset+iW-iR+iR*Math.cos(a), inset+iR+iR*Math.sin(a)]; }
    acc += cornerArc;
    if (t < acc + rightStraight) return [inset+iW, inset+iR+(t-acc)];
    acc += rightStraight;
    if (t < acc + cornerArc) { const a=(t-acc)/iR; return [inset+iW-iR+iR*Math.cos(a), inset+iH-iR+iR*Math.sin(a)]; }
    acc += cornerArc;
    if (t < acc + bottomStr) return [inset+iW-iR-(t-acc), inset+iH];
    acc += bottomStr;
    if (t < acc + cornerArc) { const a=(t-acc)/iR+Math.PI/2; return [inset+iR+iR*Math.cos(a), inset+iH-iR+iR*Math.sin(a)]; }
    acc += cornerArc;
    if (t < acc + leftStraight) return [inset, inset+iH-iR-(t-acc)];
    acc += leftStraight;
    const a=(t-acc)/iR+Math.PI; return [inset+iR+iR*Math.cos(a), inset+iR+iR*Math.sin(a)];
  };

  const segments = colors.map((color, i) => {
    const t0 = i * segLen, t1 = (i+1) * segLen;
    const pts = Array.from({length: 81}, (_, j) => ptOnRounded(t0 + (t1-t0)*j/80));
    const d = pts.map((p, j) => `${j===0?'M':'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
    return { color, d };
  });

  return (
    <svg
      style={{position:'absolute', top: 0, left: 0, pointerEvents:'none', zIndex:2, overflow:'visible'}}
      width={W} height={H}
      aria-hidden="true"
    >
      {segments.map((seg, i) => (
        <path key={i} d={seg.d} fill="none" stroke={seg.color}
          strokeWidth={bw} strokeLinecap="butt" strokeLinejoin="round"/>
      ))}
    </svg>
  );
}

export function PersonCard({ person, selected, onClick, onEdit, onQuickAdd, colorInfo, onColorChange, style, canAddParent = true, forceShowControls = false }) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const leaveTimer = useRef(null);
  const wrapperRef = useRef(null);

  React.useEffect(() => {
    if (!showColorPicker) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColorPicker]);

  const initials = person.name
    ? person.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const genderClass = person.gender === 'male'   ? 'card--male'
                    : person.gender === 'female' ? 'card--female'
                    : 'card--neutral';

  const isTouch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches;

  // Touch: Menü schliesst nicht durch „Maus verlässt Karte“, sondern durch Antippen ausserhalb
  React.useEffect(() => {
    if (!isTouch || !menuOpen) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('touchstart', handler, { passive: true });
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [isTouch, menuOpen]);

  const handleMouseEnter = () => { clearTimeout(leaveTimer.current); setHovered(true); };
  const handleMouseLeave = () => {
    if (isTouch) return;
    leaveTimer.current = setTimeout(() => { setHovered(false); setMenuOpen(false); }, 120);
  };
  const handleOption = (e, opt) => {
    e.stopPropagation(); setMenuOpen(false); setHovered(false);
    onQuickAdd && onQuickAdd(person.id, opt);
  };

  // Touch-Geräte (Handy/Tablet) haben kein „Hover“ → „+“ dort immer anzeigen.
  // Ist der Stammbaum bestellt (kein onQuickAdd), wird das „+“ nie angezeigt.
  const showPlus = !!onQuickAdd && (hovered || menuOpen || isTouch || forceShowControls);
  const { color, borderColors, symbols, outlineSymbol, goldenChildNames, isGen1 } = colorInfo || {};
  // Farbwähler nur für echte Gen1-Geschwister
  const isColorable = isGen1 && color && onColorChange;
  const cardStyle = goldenChildNames
    ? { border: '9px solid #D4A843' }
    : borderColors
      ? { border: 'none' }
      : color
        ? { borderColor: color, borderWidth: '9px', borderStyle: 'solid' }
        : {};
  const displaySymbols = symbols ? symbols.map(s => ({ type: s, outline: false })) : [];
  if (outlineSymbol) displaySymbols.unshift({ type: outlineSymbol, outline: true });

  return (
    <div
      ref={wrapperRef}
      className="person-card-wrapper"
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => { if (showColorPicker) setShowColorPicker(false); }}
    >
      <div
        className={`person-card ${genderClass} ${selected ? 'person-card--selected' : ''}`}
        style={cardStyle}
        onClick={(e) => { e.stopPropagation(); onClick && onClick(person.id); }}
        onDoubleClick={() => onEdit && onEdit(person.id)}
        title={`${person.name}${person.maidenName ? ` (geb. ${person.maidenName})` : ''}\nDoppelklick zum Bearbeiten`}
      >
        {borderColors && <MultiColorBorder colors={borderColors} />}
        <div className="card-photo">
          {person.photo ? (
            <EditedPhoto photo={person.photo} edit={person.photoEdit} alt={person.name} className="card-photo-img" fit="contain" />
          ) : (
            <div className={`card-photo-placeholder ${genderClass}`}>
              <span>{initials}</span>
            </div>
          )}
        </div>
        <div className="card-info">
          <div className="card-name">{person.name || '(Kein Name)'}</div>
          {person.maidenName && <div className="card-maiden">geb. {person.maidenName}</div>}
          {person.birthDate  && <div className="card-birth">* {person.birthDate.split('-').reverse().join('.')}</div>}
          {goldenChildNames?.length > 0 && (
            <div className="card-golden-children">{goldenChildNames.join(' · ')}</div>
          )}
        </div>

        {displaySymbols.length > 0 && (
          <div className="card-symbols">
            {displaySymbols.map((s, i) => (
              <SymbolBadge key={i} type={s.type} color={color || '#555'} size={11} outline={s.outline}/>
            ))}
          </div>
        )}
      </div>

      <button
        className={`card-add-btn${showPlus ? ' card-add-btn--visible' : ''}`}
        onMouseEnter={handleMouseEnter}
        onClick={(e) => { e.stopPropagation(); if (onQuickAdd) setMenuOpen(prev => !prev); }}
        title="Person hinzufügen"
      >+</button>

      {menuOpen && (
        <div className="card-add-menu" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {REL_OPTIONS.filter(opt => opt.direction !== 'parent' || canAddParent).map(opt => (
            <button key={opt.direction} className="card-add-menu__item" onClick={(e) => handleOption(e, opt)}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {isColorable && showPlus && !menuOpen && (
        <button
          className="card-color-btn"
          title="Familienfarbe ändern"
          onMouseEnter={handleMouseEnter}
          onClick={(e) => { e.stopPropagation(); setShowColorPicker(p => !p); }}
        >🎨</button>
      )}

      {showColorPicker && (
        <div className="card-color-picker" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {FAMILY_COLORS.map((c, i) => (
            <button
              key={i}
              className={`card-color-swatch${c === color ? ' card-color-swatch--active' : ''}`}
              style={{ background: c }}
              onClick={(e) => { e.stopPropagation(); onColorChange(person.id, i); setShowColorPicker(false); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
